import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { util } from '../../../classes/util';
import { environment } from '../../../environment';
import { ESRI } from '../../../esri-loader-util';
import { LineStyleOptions } from '../../../selectionOptions';
import { LayerService } from '../../../services/layer.service';
import { BaseComponent } from '../../base.component';

const initilizeDebounceTime = 50;

let Draw: __esri.DrawConstructor;
let Graphic: __esri.GraphicConstructor;
let GeometryService: __esri.GeometryServiceConstructor;
let LengthsParameters: __esri.LengthsParametersConstructor;

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-draw-line',
    templateUrl: './draw-line.component.html',
    styleUrls: ['../../sharedStyles/base.scss', './draw-line.component.scss']
})
export class DrawLineComponent extends BaseComponent implements OnInit {
    @Output() readonly drawComplete: EventEmitter<__esri.Graphic> = new EventEmitter();
    @Output() readonly drawLayer: EventEmitter<__esri.GraphicsLayer> = new EventEmitter();
    @Input()
    set enabled(enabled: boolean) {
        this._enabled = enabled;
        if (this._enabled) {
            this.setupHandleHiddenLayer();
            this.createPolylineDrawAction();
        } else {
            this.draw.reset();
            this.teardownHandleHiddenLayer();
            if (this.graphic) {
                this.mapView.graphics.remove(this.graphic);
            }
            this.cdr.detectChanges();
        }
    }
    _enabled: boolean;

    @Input()
    symbolProperties: any;
    @Input()
    createLayerForEachGraphic = true;

    mapView: NonNullable<__esri.MapView>;
    draw: NonNullable<__esri.Draw>;
    graphic: NonNullable<__esri.Graphic>;
    graphicsLayer: NonNullable<__esri.GraphicsLayer>;
    isLoading: boolean;

    @Input()
    showSettings: any = {
        createLayerForEachGraphic: true,
        mode: true,
        style: true,
        width: true,
        color: true,
        unit: true
    };

    defaultSymbolProperties = {
        type: 'simple-line',
        style: 'solid',
        color: 'red',
        width: 3,
        cap: 'round',
        join: 'round'
    };

    lineStyleOptions = LineStyleOptions;

    polylineModes: { name: string; value: PolylineMode; description: string }[] = [
        { value: 'click', name: 'Click', description: 'Vertices are added when the pointer is clicked.' },
        { value: 'freehand', name: 'Freehand', description: 'Vertices are added while the pointer is dragged.' },
        { value: 'hybrid', name: 'Hybrid', description: 'Vertices are added while the pointer is clicked or dragged.' }
    ];
    selectedPolylineMode = this.polylineModes[0];

    @Input()
    readonly lengthUnits: { name: string; value: number; description?: string }[] = [
        { name: 'None', value: null },
        { name: 'Inches', value: 109009, description: 'US Inch' },
        { name: 'Feet', value: 9300, description: 'US Feet' },
        { name: 'Yards', value: 109002, description: 'US Yard' },
        { name: 'Miles', value: 9035, description: 'US Mile' },
        { name: 'Centimeters', value: 109006 },
        { name: 'Meters', value: 9001 },
        { name: 'Kilometers', value: 9036 }
    ];

    @Input()
    selectedLengthUnit = this.lengthUnits[0];

    geometryService: NonNullable<__esri.GeometryService>;

    constructor(
        _vcr: ViewContainerRef,
        private readonly cdr: ChangeDetectorRef,
        private readonly layerService: LayerService
    ) {
        super(_vcr);
    }

    colorPicker: ViewContainerRef;
    @ViewChild('colorPicker') set colorPickerRef(el: ViewContainerRef) { this.colorPicker = el; }

    async ngOnInit() {
        try {
            this.symbolProperties = this.defaultSymbolProperties;
            this.mapView = await this.setupMapViewRef(this.mapView);
            [Draw, Graphic, GeometryService, LengthsParameters] = await Promise.all([ESRI.Draw, ESRI.Graphic, ESRI.GeometryService, ESRI.LengthsParameters]);
            this.draw = new Draw({ view: this.mapView });
        } catch (err) {
            this.readyProblem(err);
        } finally {
            this.isReady();
        }
    }

    async createPolylineDrawAction() {
        const action = this.draw.create('polyline', { mode: this.selectedPolylineMode.value });

        action.on(['vertex-add', 'vertex-remove', 'cursor-update'], (evt: IUpdateEvent) => {
            this.graphic = this.createPolylineGraphic(evt.vertices);
            this.mapView.graphics.add(this.graphic);
        });

        action.on('draw-complete', (evt: IDrawCompleteEvent) => {
            const completedGraphic = this.createPolylineGraphic(evt.vertices);
            this.addLineGraphic(completedGraphic);
            this.drawComplete.emit(completedGraphic);
            this.createPolylineDrawAction();
        });
    }

    private createPolylineGraphic(vertices: number[][]) {
        if (vertices.length > 1 && this.graphic) {
            this.mapView.graphics.remove(this.graphic);
        }

        const polyline = {
            type: 'polyline',
            paths: vertices,
            spatialReference: this.mapView.spatialReference
        };

        const graphic = new Graphic({
            geometry: polyline,
            symbol: this.symbolProperties
        });

        return graphic;
    }

    private async addLineGraphic(graphic: __esri.Graphic) {
        if (!this.graphicsLayer || this.createLayerForEachGraphic) {
            let title = 'Line';
            if (this.selectedPolylineMode.value === 'freehand') {
                title = `${this.selectedPolylineMode.name} ${title}`;
            }
            if (this.selectedLengthUnit.value) {
                this.isLoading = true;
                if (!this.geometryService) {
                    this.geometryService = new GeometryService({ url: environment.geometryServiceUrl });
                }
                const result: { areas: number[]; lengths: number[] } = await this.geometryService.lengths(new LengthsParameters({
                    calculationType: 'preserve-shape',
                    lengthUnit: this.selectedLengthUnit.value,
                    polylines: [graphic.geometry]
                }));
                if (result && result.lengths.length) {
                    title += ` - ${result.lengths[0]} ${this.selectedLengthUnit.name}`;
                }
                this.isLoading = false;
                this.cdr.detectChanges();
            }
            this.graphicsLayer = await this.layerService.addGraphicsLayer({ title, graphicLayer: 'line' });
        }
        this.graphicsLayer.add(graphic);
        this.drawLayer.emit(this.graphicsLayer);
        this.graphic = null;
    }

    reset() {
        this.draw.reset();
        this.createPolylineDrawAction();
    }

    initializing = false;
    private readonly listners: IHandle[] = [];
    private setupHandleHiddenLayer() {
        this.initializing = true;
        const stopInitialCheck = util.debounce(() => { this.initializing = false; }, initilizeDebounceTime);
        this.listners.push(this.layerService.mapRef.allLayers.on('before-add', (event) => {
            if (this.initializing && event.item.listMode === 'hide') {
                event.item.set('hideInLegend', true);
                event.item.set('drawLineToolGeneratedLayer', true);
                stopInitialCheck();
            }
        }));
        this.listners.push(this.layerService.mapRef.allLayers.on('after-remove', (event) => {
            if (event.item.get<boolean>('drawLineToolGeneratedLayer')) {
                this.initializing = true;
            }
        }));
    }
    private teardownHandleHiddenLayer() {
        while (this.listners.length) {
            this.listners.pop().remove();
        }
    }
}

type PolylineMode = 'hybrid' | 'freehand' | 'click';

interface IDrawCompleteEvent {
    vertices: number[][];
    preventDefault: () => void;
    defaultPrevented: boolean;
    type: 'draw-complete';
}

interface IUpdateEvent {
    vertices: number[][];
    vertexIndex: number;
    preventDefault: () => void;
    defaultPrevented: boolean;
    type: 'cursor-update' | 'vertex-add' | 'vertex-remove';
}
